import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import Swal from "sweetalert2";

interface ExportAntdColumnsProps {
  columns: any[];
  dataSource: Record<string, any>[];
  fileName?: string;
  sheetName?: string;
}

export const exportAntdTableToExcel = ({
  columns,
  dataSource,
  fileName = "Report",
  sheetName = "Sheet1",
}: ExportAntdColumnsProps): Promise<void> => {
  return new Promise(async (resolve) => {
    if (!Array.isArray(dataSource) || dataSource.length === 0) {
      await Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "No data available to export.",
        confirmButtonText: "OK",
        confirmButtonColor: "#f26522",
      });

      resolve();
      return;
    }
    const exportColumns = columns.filter(
      (col: any) =>
        (col.dataIndex || col.key === "srNo" || col.title === "Sr. No.") &&
        col.excludeFromExport !== true,
    );

    const formattedData = dataSource.map((record, index) => {
      const row: Record<string, any> = {};

      exportColumns.forEach((col: any) => {
        let header =
          typeof col.title === "string"
            ? col.title
            : (col.key ?? col.dataIndex);

        if (
          typeof col.title !== "string" &&
          col.title?.props?.children?.[0]?.props?.children
        ) {
          header = col.title.props.children[0].props.children;
        }
        let value = record[col.dataIndex];
        if (
          col.key === "srNo" ||
          col.dataIndex === "srNo" ||
          header === "Sr. No."
        ) {
          value = index + 1;
        } else if (col.excelFormatter) {
          value = col.excelFormatter(value, record, index);
        } else if (
          typeof value === "string" &&
          /^\d{4}-\d{2}-\d{2}T/.test(value)
        ) {
          const date = new Date(value);

          if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();

            value = `${day}-${month}-${year}`;
          }
        } else if (typeof value === "number") {
          if (!Number.isInteger(value)) {
            value = Number(value.toFixed(2));
          }
        } else if (
          typeof value === "string" &&
          value.trim() !== "" &&
          !isNaN(Number(value))
        ) {
          const num = Number(value);

          if (!Number.isInteger(num)) {
            value = Number(num.toFixed(2));
          }
        }

        row[header] = value ?? "";
      });

      return row;
    });
    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    worksheet["!cols"] = exportColumns.map((col: any) => ({
      wch: Math.max(
        (typeof col.title === "string" ? col.title.length : 8) + 1,
        10,
      ),
    }));

    const headerStyle = {
      fill: { fgColor: { rgb: "1F3864" } },
      font: { bold: true, color: { rgb: "FFFFFF" } },
      alignment: { vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const headerRange = XLSX.utils.decode_range(worksheet["!ref"]!);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
      if (worksheet[cellAddress]) {
        worksheet[cellAddress].s = headerStyle;
      }
    }
    const srNoColumn = exportColumns.find(
      (col: any) => col.dataIndex === "sr_no" || col.title === "Sr. No.",
    );

    if (srNoColumn) {
      const srNoColIndex = exportColumns.indexOf(srNoColumn);
      const dataRowStyle = {
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "D3D3D3" } },
          bottom: { style: "thin", color: { rgb: "D3D3D3" } },
          left: { style: "thin", color: { rgb: "D3D3D3" } },
          right: { style: "thin", color: { rgb: "D3D3D3" } },
        },
      };

      for (let row = 1; row <= headerRange.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: srNoColIndex });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = dataRowStyle;
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(blob, `${fileName}.xlsx`);
    resolve();
  });
};
